/**
 * Browser-side image downscale + re-encode.
 *
 * Phase 3 hardening: members upload phone photos that can be 8-12MB raw.
 * We display them at most ~1024-2048px wide. Resizing on upload saves
 * bandwidth (faster mobile UX), Supabase Storage costs, and download
 * time on every subsequent view.
 *
 * Read-side resize via Cloudflare Image Resizing was deferred (the
 * account has Cloudflare Stream, not Images — see the hardening
 * build log). This is the primary line of defence on image weight.
 */

export interface CompressArgs {
  /** Max dimension on either axis. Image is downscaled to fit, preserving aspect ratio. */
  maxDim?: number;
  /** JPEG / WebP quality, 0-1. */
  quality?: number;
  /** Output MIME type. Defaults to image/jpeg for broad compatibility. */
  mimeType?: 'image/jpeg' | 'image/webp';
}

const DEFAULT_MAX_DIM = 2048;
const DEFAULT_QUALITY = 0.85;
const DEFAULT_MIME = 'image/jpeg';
const HARD_SIZE_LIMIT_BYTES = 5 * 1024 * 1024; // 5MB after compression

/**
 * Compress an image File. Returns a new File at the same name with the
 * appropriate extension swapped. If the input is already smaller than
 * the target dims AND mime, returns the input unchanged.
 */
export async function compressImage(file: File, args: CompressArgs = {}): Promise<File> {
  const maxDim = args.maxDim ?? DEFAULT_MAX_DIM;
  const quality = args.quality ?? DEFAULT_QUALITY;
  const mimeType = args.mimeType ?? DEFAULT_MIME;

  // Fast path: only image inputs.
  if (!file.type.startsWith('image/')) {
    throw new Error('Not an image.');
  }

  // SVGs and animated GIFs would lose their nature through canvas re-encode.
  // Pass them through untouched.
  if (file.type === 'image/svg+xml' || file.type === 'image/gif') {
    enforceHardLimit(file);
    return file;
  }

  const bitmap = await loadBitmap(file);
  try {
    const { width: w, height: h } = scaleToFit(bitmap.width, bitmap.height, maxDim);

    // Bail out of the re-encode if the source is already small AND the
    // browser can hand back a JPEG of equivalent size — saves a CPU hit.
    if (w === bitmap.width && h === bitmap.height && file.size < 800 * 1024) {
      enforceHardLimit(file);
      return file;
    }

    const canvas = typeof OffscreenCanvas !== 'undefined'
      ? new OffscreenCanvas(w, h)
      : document.createElement('canvas');
    if (canvas instanceof HTMLCanvasElement) {
      canvas.width = w;
      canvas.height = h;
    }
    const ctx = (canvas as OffscreenCanvas | HTMLCanvasElement).getContext('2d') as
      | OffscreenCanvasRenderingContext2D
      | CanvasRenderingContext2D
      | null;
    if (!ctx) throw new Error('Canvas 2D context unavailable.');

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(bitmap, 0, 0, w, h);

    const blob = await canvasToBlob(canvas as OffscreenCanvas | HTMLCanvasElement, mimeType, quality);

    // If the re-encode happened to be larger than the original, keep the
    // original. Happens occasionally with already-JPEG inputs.
    const finalBlob = blob.size < file.size ? blob : file;

    const ext = mimeType === 'image/webp' ? 'webp' : 'jpg';
    const baseName = file.name.replace(/\.[a-z0-9]+$/i, '') || 'upload';
    const newFile = new File([finalBlob], `${baseName}.${ext}`, { type: finalBlob.type });

    enforceHardLimit(newFile);
    return newFile;
  } finally {
    if ('close' in bitmap && typeof bitmap.close === 'function') bitmap.close();
  }
}

function scaleToFit(srcW: number, srcH: number, maxDim: number): { width: number; height: number } {
  const longest = Math.max(srcW, srcH);
  if (longest <= maxDim) return { width: srcW, height: srcH };
  const scale = maxDim / longest;
  return { width: Math.round(srcW * scale), height: Math.round(srcH * scale) };
}

async function loadBitmap(file: File): Promise<ImageBitmap | HTMLImageElement> {
  // ImageBitmap is the fast path (decoded off the main thread). Fall back
  // to a regular HTMLImageElement for browsers without it.
  if (typeof createImageBitmap === 'function') {
    return await createImageBitmap(file);
  }
  return await new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = (e) => { URL.revokeObjectURL(url); reject(new Error('Image decode failed.')); };
    img.src = url;
  });
}

async function canvasToBlob(
  canvas: OffscreenCanvas | HTMLCanvasElement,
  type: string,
  quality: number,
): Promise<Blob> {
  if ('convertToBlob' in canvas) {
    return await canvas.convertToBlob({ type, quality });
  }
  return await new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error('Canvas toBlob returned null.'))),
      type,
      quality,
    );
  });
}

function enforceHardLimit(file: File): void {
  if (file.size > HARD_SIZE_LIMIT_BYTES) {
    throw new Error(
      `Image is ${(file.size / 1024 / 1024).toFixed(1)}MB after compression. The limit is 5MB. Try a smaller image.`,
    );
  }
}
